/**
 * Strategem targeting copy for banners, cancel bar, and zone hints.
 * @module targeting/getTargetingMessage
 */

import i18n from '../i18n';

/**
 * @param {Object|null|undefined} strategemTargetInfo - From `needsStrategemTarget` / drag synthetic info
 * @returns {boolean}
 */
export function isStrategemZoneTargeting(strategemTargetInfo) {
  if (!strategemTargetInfo) return false;
  const targetingOptions = strategemTargetInfo.targetingOptions?.[0];
  return (
    strategemTargetInfo.type === 'zone' ||
    targetingOptions?.targeting === 'zone'
  );
}

/**
 * Dynamic prompt (localized), e.g. named strategem vs zone targeting.
 * @param {Object|null|undefined} strategemTargetInfo
 * @param {{ displayName?: string | null } | undefined} [options] - UI-localized order name when available (from /order/:id?lang=…)
 * @returns {string}
 */
export function getStrategemTargetingPrompt(strategemTargetInfo, options) {
  const override =
    options && typeof options.displayName === 'string' && options.displayName.trim().length > 0
      ? options.displayName.trim()
      : '';
  const rawName = override || strategemTargetInfo?.strategemName;
  const name =
    typeof rawName === 'string' && rawName.trim().length > 0
      ? rawName.trim()
      : i18n.t('battleUI.strategemGenericName');
  const base = isStrategemZoneTargeting(strategemTargetInfo)
    ? i18n.t('battleUI.selectZoneForStrategemNamed', { name })
    : i18n.t('battleUI.selectTargetForStrategemNamed', { name });

  const sid = Number(strategemTargetInfo?.strategemId);
  const outcome = strategemTargetInfo?.fractureMindsOutcome;
  if (sid === 59 && outcome === 'destroy') {
    return `${base} ${i18n.t('battleUI.fractureMindsTargetingOutcomeDestroy')}`;
  }
  if (sid === 59 && outcome === 'immobilize') {
    return `${base} ${i18n.t('battleUI.fractureMindsTargetingOutcomeImmobilize')}`;
  }

  return base;
}

// Get targeting mode messages for UI display (banner / status line)
function getTargetingMessage(
  deployTargetMode,
  selectedDeployOrderId,
  deployTargetInfo,
  error,
  strategemTargetMode,
  selectedStrategemId,
  strategemTargetInfo
) {
  if (deployTargetMode && selectedDeployOrderId && !error) {
    const deployName =
      typeof deployTargetInfo?.order?.name === 'string' && deployTargetInfo.order.name.trim().length > 0
        ? deployTargetInfo.order.name.trim()
        : i18n.t('battleUI.deployTargetingFallbackUnit');
    return {
      show: true,
      message: i18n.t('battleUI.selectTargetForDeployNamed', { name: deployName }),
      type: 'deploy'
    };
  }
  if (strategemTargetMode && selectedStrategemId && !error) {
    return {
      show: true,
      message: getStrategemTargetingPrompt(strategemTargetInfo, {}),
      type: 'strategem'
    };
  }

  return {
    show: false,
    message: '',
    type: null
  };
}

export default getTargetingMessage;
